import classNames from "classnames";
import React from "react";
import { TopMenuItem } from "~/models/menu";

interface PanelEntry {
  title: string; // title shown in the submenu header
  url: string; // url of the parent menu item (used for "View All" link)
  items: TopMenuItem[];
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function BackArrowIcon() {
  return (
    <svg
      width="24px"
      height="12px"
      viewBox="0 0 24 12"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>back_arrow@2x</title>
      <g
        id="System_Icons"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g
          id="System/Icons"
          transform="translate(-242.000000, -279.000000)"
          fill="#355FFE"
        >
          <g id="Group" transform="translate(242.000000, 273.000000)">
            <g
              id="streamline-icon-keyboard-arrow-right@48x48"
              transform="translate(0.000000, 6.000000)"
            >
              <path
                d="M23.7099835,5.28939237 L18.9299868,0.300791358 C18.6424263,0.00466407551 18.2123844,-0.0818037924 17.8399876,0.0816268783 C17.4665623,0.241725068 17.2220031,0.620456438 17.219988,1.04177603 L17.219988,4.17269716 C17.219988,4.31679383 17.1080593,4.43360726 16.9699882,4.43360726 L1.5,4.43360726 C0.671572407,4.43360726 0,5.13448783 0,5.99906782 C0,6.86364782 0.671572407,7.56452839 1.5,7.56452839 L16.9999881,7.56452839 C17.1380592,7.56452839 17.249988,7.68134182 17.249988,7.82543849 L17.249988,10.9563596 C17.249988,11.5327463 17.6977029,12 18.2499873,12 C18.517233,11.9988422 18.7729155,11.8860925 18.9599868,11.6869079 L23.7399834,6.70874328 C23.8984545,6.52031954 23.9902247,6.28088176 24,6.03037704 C24.00152,5.75297431 23.8971698,5.48634696 23.7099835,5.28939237 Z"
                fill="#355FFE"
                id="Path"
              ></path>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="12px"
      height="18px"
      viewBox="0 0 12 18"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>right_arrow@2x</title>
      <g
        id="System_Icons"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g id="System/Icons" transform="translate(-249.000000, -321.000000)">
          <g
            id="atom/icon/arrow-right"
            transform="translate(245.000000, 321.000000)"
          >
            <rect
              id="Rectangle"
              x="0"
              y="2.27373675e-13"
              width="18"
              height="18"
            ></rect>
            <path
              d="M15.2416855,8.97226877 C15.2416855,9.49591419 15.017266,9.94475312 14.6432336,10.2439791 L6.26490689,17.6498214 C5.66645498,18.1734668 4.76877712,18.0986604 4.31993819,17.5002085 C3.87109926,16.9017565 3.87109926,16.0788852 4.46955117,15.5552398 L11.800587,9.12188174 C11.8753935,9.04707525 11.8753935,8.97226877 11.800587,8.82265579 C11.800587,8.82265579 11.800587,8.82265579 11.800587,8.82265579 L4.46955117,2.38929778 C3.87109926,1.86565236 3.87109926,0.967974498 4.39474468,0.444329079 C4.9183901,-0.0793163405 5.74126147,-0.154122829 6.33971338,0.294716102 L14.7180401,7.62575197 C15.017266,7.99978442 15.2416855,8.44862335 15.2416855,8.97226877 Z"
              id="Path"
              fill="#355FFE"
            ></path>
          </g>
        </g>
      </g>
    </svg>
  );
}

// ─── Menu row ─────────────────────────────────────────────────────────────────

interface MenuRowProps {
  item: TopMenuItem;
  className?: string;
  onOpen: (item: TopMenuItem) => void;
}

function MenuRow({ item, className, onOpen }: MenuRowProps) {
  const hasChildren = (item.children?.length ?? 0) > 0;

  return (
    <div className="tw:flex tw:justify-between tw:items-center tw:border-b! tw:border-gray-300! last:tw:border-b-0">
      <a
        href={item.url}
        className="tw:flex tw:flex-col tw:px-5 tw:min-w-0 hover:tw:bg-blue-50 tw:transition-colors tw:group"
      >
        <span
          className={classNames(
            "tw:text-md tw:text-gray-800 group-hover:tw:text-blue-600 tw:transition-colors tw:text-nowrap",
            className ? className : "",
          )}
        >
          {item.title}
        </span>
      </a>
      {hasChildren ? (
        <div
          aria-label={`Open ${item.title} submenu`}
          onClick={() => onOpen(item)}
          className="tw:flex tw:items-center tw:justify-center tw:cursor-pointer tw:w-12 tw:h-full tw:min-h-[3.2rem] tw:transition-colors"
        >
          <ChevronRightIcon />
        </div>
      ) : (
        <div className="tw:w-12 tw:h-full tw:min-h-[3.2rem]" />
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

interface PanelProps {
  panel: PanelEntry;
  canGoBack: boolean;
  onOpen: (item: TopMenuItem) => void;
  onBack: () => void;
}

function Panel({ panel, canGoBack, onOpen, onBack }: PanelProps) {
  return (
    <div
      className={classNames(
        "tw:flex tw:flex-col tw:w-full tw:bg-white",
        canGoBack && "tw:fixed tw:min-h-[400px]", // ensure the panel is tall enough to cover other buttons
      )}
    >
      {/* Header (shown for all submenus) */}
      {canGoBack && (
        <div className="tw:flex tw:items-center tw:border-b! tw:border-gray-300!">
          <div
            aria-label="Back"
            onClick={onBack}
            className="tw:flex tw:items-center tw:cursor-pointer tw:gap-1 tw:px-4 tw:py-3 tw:text-sm tw:font-medium tw:text-blue-600 hover:tw:text-blue-800 hover:tw:bg-blue-50 tw:transition-colors tw:rounded-md tw:m-1"
            style={{ transform: "scale(-1)" }}
          >
            <BackArrowIcon />
          </div>
          <span className="tw:flex-1 tw:text-center tw:text-md tw:font-semibold! tw:text-gray-900! tw:pr-16">
            {panel.title}
          </span>
        </div>
      )}

      {/* Items */}
      <div>
        {panel.items.map((item) => (
          <MenuRow
            key={item.id}
            item={item}
            className={canGoBack ? "tw:font-medium!" : "tw:font-semibold!"}
            onOpen={onOpen}
          />
        ))}
      </div>

      {/* Footer (shown for all submenus) */}
      {canGoBack && (
        <div className="tw:flex tw:items-center tw:bg-white tw:border-b! tw:border-gray-300! tw:py-10">
          <div className="tw:border-b tw:border-blue-600 tw:m-1 tw:py-1 tw:mx-4 ">
            <a
              aria-label="All"
              href={panel.url}
              className="tw:flex tw:items-center tw:cursor-pointer tw:gap-1 tw:text-md tw:text-blue-600! hover:tw:text-blue-800 hover:tw:bg-blue-50 tw:transition-colors"
            >
              View All
              <BackArrowIcon />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mobile menu ──────────────────────────────────────────────────────────────

export function MobileMenu(props: { id: number; items: TopMenuItem[] }) {
  const [stack, setStack] = React.useState<PanelEntry[]>([
    { title: "", url: "", items: props.items },
  ]);

  // Always show the top two panels: the one sliding out and the one sliding in.
  // This keeps the animation smooth at any depth.
  const currentPanel = stack[stack.length - 1];
  const prevPanel = stack[stack.length - 2] ?? null;
  const isNested = stack.length > 1;

  function openSubmenu(item: TopMenuItem) {
    if ((item.children?.length ?? 0) > 0) {
      setStack((prev) => [
        ...prev,
        { title: item.title, url: item.url, items: item.children! },
      ]);
    }
  }

  function goBack() {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }

  return (
    <div className="tw:relative tw:lg:hidden tw:overflow-hidden tw:bg-white tw:w-full">
      {/* ── Outgoing panel ── */}
      <div
        className="tw:z-[1001]"
        style={{ transform: isNested ? "translateX(-100%)" : "translateX(0)" }}
      >
        {prevPanel ? (
          <Panel
            panel={prevPanel}
            canGoBack={stack.length > 2}
            onOpen={openSubmenu}
            onBack={goBack}
          />
        ) : (
          <Panel
            panel={{ title: "", url: "", items: props.items }}
            canGoBack={false}
            onOpen={openSubmenu}
            onBack={goBack}
          />
        )}
      </div>

      {/* ── Incoming panel  ── */}
      <div
        className="tw:fixed tw:inset-0 tw:bg-white tw:z-[1001]"
        style={{
          transform: isNested ? "translateX(0)" : "translateX(100%)",
          top: "50px",
        }}
      >
        <Panel
          panel={currentPanel}
          canGoBack={isNested}
          onOpen={openSubmenu}
          onBack={goBack}
        />
      </div>
    </div>
  );
}
